import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { ProgressBar, ReloadPageNotification } from 'components/ui';
import { FormRequestRouter } from 'components/routers';

import { formRequestSelector } from 'store/reducers/formRequest.reducer';
import { abandonedFormRequest, draftFormRequest, setFormRequestReset } from 'store/actions/formRequest.action';
import { dependentsSelector } from 'store/reducers/dependents.reducer';
import { dependentDeatils, dependentReset } from 'store/actions/dependents.action';
import { RouterPrompt } from 'components/modals/Prompt';
import { authSelector } from 'store/reducers/auth.reducer';
import moment from 'moment';
import { filesToBase64 } from 'utils';
import path from 'path'

const whitelist = ['/form-request/provider', '/form-request/patient', '/form-request/create-patient', '/form-request/type', '/form-request/upload-type', '/form-request/auth-type', '/form-request/authorization', '/form-request/govt-id', '/form-request/supporting-docs', '/form-request/request-info', '/form-request/payment', '/form-request/add-payment', '/form-request/confirm', '/form-request/requestConfirmed']

export const FormRequestLayout = () => {
  const dispatch = useDispatch();
  const { currentStep, total, showPrompt, ...request } = useSelector(formRequestSelector);
  const { detailed } = useSelector(dependentsSelector)
  const { profile } = useSelector(authSelector)
  let patientInfo = request.patientInfo || { dependent_id: request.patientId || '' };

  useEffect(() => {
    return () => {
      dispatch(setFormRequestReset());
    };
  }, [dispatch]);
  useEffect(() => {
    if (request.patientId) {
      if (!detailed) dispatch(dependentDeatils(request.patientId));
      else if (detailed.id != request.patientId) dispatch(dependentDeatils(request.patientId));
    }
  }, [request.patientId])

  useEffect(() => {
    if ((currentStep == 2 && !request.rid) || currentStep == 6) {
      handleSave('draft', (err, message) => {})
    }
  }, [currentStep])
  const selectedLocation = request.provider?.selectedLocation
  const handleSave = async (status: string, callback?: (error: boolean, message: string) => void) => {
    let formInfo = request.fileData || {};
    if (formInfo.form_name) {
      let extension = formInfo.form_name.substring(formInfo.form_name.lastIndexOf('.') + 1);
      formInfo.form_name = `Form_${profile?.last_name?.slice(0, 1).toUpperCase()}_${profile?.name}_${moment().format('MMDDYY')}.`+extension
    }
    let reasonForRequest = request.requestDetails?.reasonForRequest as { value: string } | { value: string }[];
    let data = {
      ...patientInfo,
      ...formInfo,
      form_id: request.typeId || '',
      custodian_id: `${request.provider.id}`,
      card_id: request.paymentMethod !== 'check' && request.payment || '',
      is_upload_authorization: request.authType == 'GeneralRelease',
      initials: request.estimateInitials,
      check_payment: request.paymentMethod == 'check',
      delivery_method: request.requestDetails?.deliveryMethod,
      scope: request.requestDetails?.recordType?.map(item => item.value),
      reason_for_request: Array.isArray(reasonForRequest)? reasonForRequest.map(item => item.value): reasonForRequest? [reasonForRequest.value] : [],
      rush_request: request.requestDetails?.rushRequest,
      rid: request.rid,
      status,
      current_step: currentStep,
      selected_location: selectedLocation
    }

    if(request?.govtId?.frontFileId && data.frontFile) data.frontFileId = request?.govtId?.frontFileId;
    if(request?.govtId?.frontFileId && data.frontFile) data.frontType = 'front';
    if(request?.govtId?.backFileId && data.backFile) data.backFileId = request?.govtId?.backFileId;
    if(request?.govtId?.backFileId && data.backFile) data.backType = 'back'
    
    if (request.requestDetails?.deliveryMethod == 'fax-return') {
      data.fax_number = request.requestDetails.faxNumber
      data.recipient_name = request.requestDetails.recipientName
      data.recipient_type = request.requestDetails.recipientType?.value
      data.remark = request.requestDetails.remark
    }
    const signature = request.auth?.signature ? (await filesToBase64([request.auth?.signature])) : ''
    let fd = new FormData()
    for (let key in data) fd.append(key, data[key] || '')
    fd.append('auth_name', request.auth?.fullName || '')
    fd.append('auth_initials', request.auth?.initials || '')
    fd.append('auth_dob', request.auth?.birthday ? moment(request.auth?.birthday).format(process.env.REACT_APP_DATE_FORMAT) : moment().format(process.env.REACT_APP_DATE_FORMAT))
    fd.append('auth_ssn', request.auth?.ssn ? request.auth?.ssn.replace('-', '').replace('-', '') : '')
    fd.append('auth_role', request.auth?.iAmThe || '')
    fd.append('auth_extra_field', request.auth?.extraField || '')
    fd.append('auth_extra_value', request.auth?.extraValue || '')
    fd.append('auth_signature', signature)
    fd.append('auth_signature_id', '1234')
    fd.append('auth_print_name', request.auth?.fullName || '')
    fd.append('auth_date', moment().format(process.env.REACT_APP_DATE_FORMAT))
    
    if (request.govtId?.frontFile) fd.append('frontFile', request.govtId?.frontFile, `${profile?.name || ''}_front${path.extname(request.govtId?.frontFile.name)}`)
    if (request.govtId?.backFile) fd.append('backFile', request.govtId?.backFile, `${profile?.name || ''}_back${path.extname(request.govtId?.backFile.name)}`)
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    request.supportingDocs?.filter(doc => doc.deleted || doc.suppFile).forEach((doc, i) => {
      fd.append(`supporting_docs[document][${i}]`, doc.suppFile || JSON.stringify({ id: doc.fileId }))
    })
    if (request?.auth?.generalReleaseFile) fd.append('authorization[][document]', request.auth.generalReleaseFile)
    dispatch(status == 'draft' ? draftFormRequest(fd, callback) : abandonedFormRequest(fd));
    dispatch(dependentReset());
    localStorage.setItem('language','en')
    return true
  }

  return (
    <>
      <RouterPrompt
        when={showPrompt || false}
        title='Exiting Form Request Process'
        description={[`You are about to exit the form request process and your request for the records of`, `${request.patientInfo?.patient_first_name || detailed?.firstName || profile?.name} will be abandoned.`]}
        okText='Abandon'
        onOk={() => handleSave('abandoned')}
        cancelText='Continue'
        onCancel={() => null}
        otherOkText='Save as Draft'
        otherOnOk={() => handleSave('draft')}
        whitelist={whitelist}
        hideOk={['Retrieval', 'Retrieval - Former Client'].includes(request?.provider?.service_level)}
      />

      {currentStep > 1 && <ReloadPageNotification />}
      <ProgressBar total={total} current={currentStep} />
      <FormRequestRouter curruntStep={currentStep} total={total} />
    </>
  );
};
